/**
 * Megamenü-Variante der Hauptnavigation (RED-12852).
 *
 * Je Hauptnavigationspunkt mit Unterelementen liegt ein Panel als direktes Kind der Komponente. Desktop
 * (ab 1024px): Das Panel ist ein popover="auto" und liegt im Top-Layer vom unteren Rand der Leiste bis zum
 * Viewport-Ende. Mobil: Dasselbe Panel wird in den Drilldown des Burger-Dialogs gehängt (Task A4). Ein Panel ist nie
 * gleichzeitig Popover und Dialog-Inhalt; der Modus hängt am matchMedia-Listener.
 *
 * Das JS läuft auch, wenn der A/B-Wrapper diesen Slot versteckt (hidden); alle Messungen passieren erst beim Öffnen.
 */
const DESKTOP_MEDIA = '(min-width: 1024px)';
const SCROLL_LOCK_KLASSE = 'megamenue-offen';

/**
 * Horizontaler Scroll-Bereich mit Pfeil-Buttons: bedient die Desktop-Leiste, die Kachelzeile und den Spaltenbereich.
 * Pfeile erscheinen nur bei Maus-Geräten (controls.css) und nur, wenn erstes bzw. letztes Element nicht voll sichtbar
 * ist. Root des IntersectionObservers ist der Scroll-Container selbst, nicht die Komponente: Panels liegen im
 * Top-Layer außerhalb der Komponenten-Box.
 */
class Slider {
    constructor(wurzel) {
        this.liste = wurzel.querySelector(':scope > .scroll');
        this.controls = wurzel.querySelector(':scope > .controls');
        if (!this.liste || !this.controls) {
            return;
        }
        this.items = Array.from(this.liste.querySelectorAll(':scope > li'));
        this.prev = this.controls.querySelector('.prev');
        this.next = this.controls.querySelector('.next');
        if (this.items.length === 0) {
            return;
        }
        this.next.addEventListener('click', () => this.scrollNext());
        this.prev.addEventListener('click', () => this.scrollPrev());
        this.controls.setAttribute('active', '');
        this.initControlObservers();
    }

    scrollNext() {
        const nextItem = this.getNextVisibleItem();
        this.scrollToPosition(nextItem.offsetLeft);
    }

    scrollPrev() {
        const prevItem = this.getPrevVisibleItem();
        if (prevItem) {
            // Element am rechten Rand des Containers positionieren, nie negativ scrollen.
            const scrollPosition = prevItem.offsetLeft + prevItem.offsetWidth - this.liste.offsetWidth;
            this.scrollToPosition(Math.max(0, scrollPosition));
        }
    }

    getNextVisibleItem() {
        const containerRect = this.liste.getBoundingClientRect();
        for (const item of this.items) {
            if (item.getBoundingClientRect().right > containerRect.right) {
                return item;
            }
        }
        return this.items[this.items.length - 1];
    }

    getPrevVisibleItem() {
        const containerRect = this.liste.getBoundingClientRect();
        let lastVisibleItem = null;
        for (let i = this.items.length - 1; i >= 0; i--) {
            const item = this.items[i];
            const itemRect = item.getBoundingClientRect();
            if (itemRect.left < containerRect.right) {
                if (itemRect.left < containerRect.left) {
                    return item;
                }
                if (!lastVisibleItem) {
                    lastVisibleItem = item;
                }
            }
        }
        if (lastVisibleItem) {
            const index = this.items.indexOf(lastVisibleItem);
            if (index > 0) {
                return this.items[index - 1];
            }
        }
        return this.items[0];
    }

    scrollToPosition(x) {
        this.liste.scrollTo({ top: 0, left: x, behavior: 'smooth' });
    }

    initControlObservers() {
        const first = this.items[0];
        const last = this.items[this.items.length - 1];
        this.observer = new IntersectionObserver(entries => {
            entries.forEach(entry => {
                const control = entry.target === first ? this.prev : this.next;
                const vollSichtbar = entry.isIntersecting && entry.intersectionRatio >= 1;
                control.style.display = vollSichtbar ? 'none' : 'inline-flex';
            });
        }, { root: this.liste, threshold: [0.8, 1] });
        this.observer.observe(first);
        this.observer.observe(last);
    }
}

class MainNavigationMegamenue extends HTMLElement {
    connectedCallback() {
        if (this.initialisiert) {
            return;
        }
        this.initialisiert = true;

        this.desktopMedia = window.matchMedia(DESKTOP_MEDIA);
        this.popoverUnterstuetzt = typeof HTMLElement.prototype.showPopover === 'function';
        this.desktopLeiste = this.querySelector('.komponente-container--desktop nav');
        this.panels = Array.from(this.querySelectorAll(':scope > .megamenue-panel'));

        this.overlay = this.querySelector('dialog.overlay-navigation');
        this.buttonOpenNaviDialog = this.querySelector('.open-navi-overlay');
        this.ebene1 = this.querySelector('.ebene-1');
        this.drilldown = this.querySelector('.drilldown');
        this.drilldownInhalt = this.querySelector('.drilldown-inhalt');
        this.zurueckButton = this.querySelector('.zurueck-button');
        this.zuletztGeklickt = null;
        // Werte von html.style.overflow und html.style.scrollbarGutter vor dem Scroll-Lock, siehe scrollLockAktualisieren().
        this.vorherigerOverflow = '';
        this.vorherigerScrollbarGutter = '';

        this.initMobilListener();
        this.beobachteSlot();

        this.initPanelListener();
        this.initFallback();
        this.desktopMedia.addEventListener('change', () => this.modusAnwenden());
        window.addEventListener('resize', () => {
            if (this.offenesPanel()) {
                this.positioniere();
            }
        });
        this.initSlider();
        this.modusAnwenden();
    }

    initPanelListener() {
        this.panels.forEach(panel => {
            panel.addEventListener('beforetoggle', event => {
                if (event.newState === 'open') {
                    this.positioniere();
                }
            });
            panel.addEventListener('toggle', () => this.scrollLockAktualisieren());
            // Schildfläche: Klick außerhalb der weißen Fläche schließt, ohne die Seite darunter zu treffen.
            panel.addEventListener('click', event => {
                if (this.istPopover(panel) && !event.target.closest('.panel-flaeche')) {
                    panel.hidePopover();
                }
            });
            // Ein auto-Popover schließt nicht von selbst, wenn der Fokus es verlässt (WCAG 2.4.11). Verlässt der
            // Fokus Panel und Leiste in Richtung Seiteninhalt, wird geschlossen. relatedTarget === null (Fokus
            // verlässt Dokument/Fenster oder Klick ins Leere) bleibt offen, das deckt Light-Dismiss/Schildfläche ab.
            panel.addEventListener('focusout', event => {
                const ziel = event.relatedTarget;
                if (this.istPopover(panel) && ziel !== null && !panel.contains(ziel) && !this.desktopLeiste.contains(ziel)) {
                    panel.hidePopover();
                }
            });
        });
    }

    /**
     * Ohne Popover-API bleiben die Panels per CSS unsichtbar; ein Klick auf den Hauptpunkt führt dann direkt zur
     * Sortimentsseite, damit der Punkt nicht ins Leere geht.
     */
    initFallback() {
        if (this.popoverUnterstuetzt) {
            return;
        }
        this.querySelectorAll('.hauptpunkt-button').forEach(button => {
            button.addEventListener('click', () => window.location.assign(button.dataset.href));
        });
    }

    initMobilListener() {
        this.buttonOpenNaviDialog.addEventListener('click', () => {
            this.overlay.dataset.animation = 'default';
            this.overlay.showModal();
        });
        this.querySelectorAll('.kategorie-button').forEach(button => {
            button.addEventListener('click', () => this.drilldownOeffnen(button));
        });
        this.zurueckButton.addEventListener('click', () => this.zurueck());
        // Erneutes Öffnen startet immer auf der ersten Ebene.
        this.overlay.addEventListener('close', () => this.drilldownSchliessen());
    }

    initSlider() {
        const wurzeln = [this.desktopLeiste, ...this.querySelectorAll('.slider')];
        this.slider = wurzeln.filter(Boolean).map(wurzel => new Slider(wurzel));
    }

    /** Hängt das Panel des Buttons in den Drilldown-Host und blendet die erste Ebene aus. */
    drilldownOeffnen(button) {
        const panel = this.panels.find(kandidat => kandidat.id === button.dataset.panel);
        if (!panel) {
            return;
        }
        const vorheriges = this.drilldownInhalt.firstElementChild;
        if (vorheriges && vorheriges !== panel) {
            vorheriges.hidden = true;
            this.appendChild(vorheriges);
        }
        this.drilldownInhalt.appendChild(panel);
        panel.hidden = false;
        this.ebene1.hidden = true;
        this.drilldown.hidden = false;
        this.zuletztGeklickt = button;
        this.zurueckButton.focus();
    }

    /** Stellt die erste Ebene wieder her; das Panel wandert zurück an die Wurzel. */
    drilldownSchliessen() {
        const panel = this.drilldownInhalt.firstElementChild;
        if (panel) {
            panel.hidden = !this.desktopMedia.matches;
            this.appendChild(panel);
        }
        this.drilldown.hidden = true;
        this.ebene1.hidden = false;
    }

    zurueck() {
        this.drilldownSchliessen();
        this.zuletztGeklickt?.focus();
    }

    /**
     * Versteckt der Wrapper (Admin-Vorschau, Kameleoon) diesen Slot, verschwinden offene Popover/Dialog aus dem
     * Top-Layer, ihr Zustand bliebe aber offen und der Scroll-Lock hinge fest. Deshalb alles schließen.
     */
    beobachteSlot() {
        const slot = this.parentElement;
        if (!slot) {
            return;
        }
        this.slotObserver = new MutationObserver(() => {
            if (slot.hidden) {
                this.allesSchliessen();
            }
        });
        this.slotObserver.observe(slot, { attributes: true, attributeFilter: ['hidden'] });
    }

    disconnectedCallback() {
        this.slotObserver?.disconnect();
    }

    /**
     * Desktop: Panels an der Wurzel, popover="auto". Mobil: kein popover-Attribut, Panels hidden, sofern sie nicht
     * gerade im Drilldown-Host liegen (Task A4).
     */
    modusAnwenden() {
        const desktop = this.desktopMedia.matches;
        if (desktop) {
            if (this.overlay.open) {
                this.overlay.close();
            }
            this.drilldownSchliessen();
        }
        this.panels.forEach(panel => {
            if (desktop) {
                if (panel.parentElement !== this) {
                    this.appendChild(panel);
                }
                panel.hidden = false;
                if (this.popoverUnterstuetzt) {
                    panel.setAttribute('popover', 'auto');
                }
            } else {
                if (this.istPopover(panel)) {
                    panel.hidePopover();
                }
                panel.removeAttribute('popover');
                if (!panel.closest('.drilldown-inhalt')) {
                    panel.hidden = true;
                }
            }
        });
        this.scrollLockAktualisieren();
    }

    /** Oberer Rand des Panels = unterer Rand der Desktop-Leiste, als CSS-Variable am Host. */
    positioniere() {
        const rect = this.desktopLeiste.getBoundingClientRect();
        this.style.setProperty('--megamenue-top', `${Math.max(0, Math.round(rect.bottom))}px`);
    }

    /**
     * Der thalia-cli-Build präfixiert jede CSS-Regel mit dem Komponenten-Tag, eine globale Regel auf `html` ist
     * darüber nicht erreichbar. Deshalb setzt das JS neben der Klasse (bleibt als Hook erhalten) den Inline-Style.
     * Die Methode wird bei jedem toggle-Event aufgerufen, auch ohne echten Zustandswechsel (z. B. beim Wechsel
     * zwischen zwei Panels, wo die toggle-Events in beliebiger Reihenfolge eintreffen) — daher nur beim
     * tatsächlichen Übergang sichern bzw. wiederherstellen, sonst würde ein doppeltes Sichern den ursprünglichen
     * Wert mit 'hidden' überschreiben.
     *
     * Mit `overflow: hidden` verschwindet der Scrollbalken, der Viewport wird breiter und die zentrierte Seite springt
     * um die halbe Balkenbreite nach rechts. `scrollbar-gutter: stable` hält den Platz frei, wird aber nur gesetzt,
     * wenn die Seite gerade einen klassischen Scrollbalken hat, sonst würde es umgekehrt einen Sprung erzeugen.
     */
    scrollLockAktualisieren() {
        const html = document.documentElement;
        const offen = Boolean(this.offenesPanel());
        if (offen === html.classList.contains(SCROLL_LOCK_KLASSE)) {
            return;
        }
        html.classList.toggle(SCROLL_LOCK_KLASSE, offen);
        if (offen) {
            this.vorherigerOverflow = html.style.overflow;
            this.vorherigerScrollbarGutter = html.style.scrollbarGutter;
            if (window.innerWidth > html.clientWidth) {
                html.style.scrollbarGutter = 'stable';
            }
            html.style.overflow = 'hidden';
        } else {
            html.style.overflow = this.vorherigerOverflow;
            html.style.scrollbarGutter = this.vorherigerScrollbarGutter;
        }
    }

    offenesPanel() {
        return this.panels.find(panel => this.istPopover(panel)) ?? null;
    }

    /** true, wenn das Panel gerade als Popover offen ist. In Browsern ohne Popover-API immer false. */
    istPopover(panel) {
        return this.popoverUnterstuetzt && panel.hasAttribute('popover') && panel.matches(':popover-open');
    }

    /** Schließt offene Panels (und in Task A4 den Dialog), z. B. wenn der Wrapper den Slot versteckt. */
    allesSchliessen() {
        this.panels.forEach(panel => {
            if (this.istPopover(panel)) {
                panel.hidePopover();
            }
        });
        if (this.overlay.open) {
            this.overlay.close();
        }
    }
}

if (!customElements.get('main-navigation-megamenue')) {
    customElements.define('main-navigation-megamenue', MainNavigationMegamenue);
}
